import { ArrowRight, type LucideIcon } from "lucide-react";
import type { JSX } from "react";
import { Link } from "react-router";

import { cn } from "../../lib/cn.js";

import { InfoHint } from "./InfoHint.js";

export interface KpiCardProps {
  icon: LucideIcon;
  label: string;
  value: number | string;
  /** Secondary metric — e.g. "2 critical", "60 last 7d". */
  hint?: string;
  /** Explains what the metric measures — shown in a tooltip on an info icon. */
  info?: string;
  /** Optional accent class for the icon container (e.g. "bg-amber-100 text-amber-700"). */
  accentClassName?: string;
  loading?: boolean;
  to?: string;
}

export function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  info,
  accentClassName,
  loading,
  to,
}: KpiCardProps): JSX.Element {
  const body = (
    <>
      <div
        className={cn(
          "flex h-12 w-12 shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-105",
          accentClassName ?? "bg-zinc-100 text-foreground",
        )}
      >
        <Icon className="h-6 w-6" aria-hidden />
      </div>
      <div className="flex flex-1 flex-col">
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          {label}
          {info ? <InfoHint text={info} label={`What is ${label}?`} /> : null}
        </span>
        <span className="text-2xl font-semibold tabular-nums text-foreground">
          {loading ? "—" : value}
        </span>
        {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </div>
      {to ? (
        <ArrowRight
          className="h-4 w-4 shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100"
          aria-hidden
        />
      ) : null}
    </>
  );

  const base = "group flex items-center gap-4 rounded-lg border bg-card p-4 transition-all";

  if (to) {
    return (
      <Link
        to={to}
        className={cn(
          base,
          "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        )}
      >
        {body}
      </Link>
    );
  }

  return <div className={base}>{body}</div>;
}
